import React, { useCallback, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Stack from '@mui/material/Stack';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import { PORTAL_ROUTE } from '../../constants/routes';
import ClientHealthIssuesModal from './ClientHealthIssuesModal';
import UnknownEnforcementModal from './UnknownEnforcementModal';
import featureFlags from 'src/utils/featureFlags';
import { objectToQueryString } from 'src/utils/uri';
import {
  useDashboardClientHealth,
  useDashboardEnforcement,
  useDashboardEnvironment,
  useDashboardLogCollectorVersions,
  useInfiniteDashboardManagedInstallers,
  useInfiniteDashboardPolicies,
} from 'src/hooks/dashboard';
import { useWdacReadinessStatusCounts } from 'src/hooks/device';
import { useTagOptions } from 'src/hooks/tag';
import ListActionBar from 'src/components/ListActionBar';
import { readFilterParams, useFilterParams } from 'src/hooks/filterParams';
import { DEVICE_STATUS } from 'src/utils/enums/deviceStatus';
import { HEALTH_STATUS, getHealthStatusLabel } from 'src/utils/enums/healthStatus';
import {
  READINESS_STATUS,
  getReadinessStatusColor,
  getReadinessStatusLabel,
} from 'src/utils/enums/readinessStatus';
import { WDAC_STATUS, getWdacStatusLabel } from 'src/utils/enums/wdacStatus';
import DistributionBarChart, {
  DISTRIBUTION_PAGE_SIZE,
} from 'src/features/charts/DistributionBarChart';
import StatusBarChart from 'src/features/charts/StatusBarChart';
import StatusDonutChart from 'src/features/charts/StatusDonutChart';
import { DATE_RANGE_COLORS, rampColor } from 'src/features/charts/palette';
import { WDAC_MANAGED_INSTALLERS_PREVIOUS_PATH } from 'src/pages/wdac-results/tabs/WdacManagedInstallers/constants';

const buildDevicesLink = query => {
  const queryString = objectToQueryString(query);
  return `${PORTAL_ROUTE}/devices${queryString ? `?${queryString}` : ''}`;
};

const CHART_GRID_SX = {
  display: 'grid',
  gap: 1.5,
  gridTemplateColumns: { xs: '1fr', lg: 'repeat(2, minmax(0, 1fr))' },
  gridAutoRows: 'auto',
  alignItems: 'stretch',
};

// The distribution cards are rows of the same grid rather than a separate stack, so one gap governs
// every seam on the page. Hoisted because the chart components are memoized.
const FULL_WIDTH_CARD_SX = { gridColumn: '1 / -1' };

// Enforcement state is a spread across the fleet rather than a status with its own chip colours,
// so it borrows the shared range palette in fixed order.
const ENFORCEMENT_COLORS = {
  [WDAC_STATUS.Enforced]: DATE_RANGE_COLORS[0],
  [WDAC_STATUS.Audit]: DATE_RANGE_COLORS[1],
  [WDAC_STATUS.Unknown]: DATE_RANGE_COLORS[2],
};

// The two status charts read as a pair, so health borrows the readiness green/orange/red here
// rather than its own chip palette, which stays as it is everywhere a health chip is rendered.
const CLIENT_HEALTH_CHART_COLORS = {
  [HEALTH_STATUS.Healthy]: getReadinessStatusColor(READINESS_STATUS.Ready),
  [HEALTH_STATUS.ConfigIssue]: getReadinessStatusColor(READINESS_STATUS.AlmostReady),
  [HEALTH_STATUS.Offline]: getReadinessStatusColor(READINESS_STATUS.NeedsWork),
};

const DevicesCharts = () => {
  const useLegacyClientHealthIssuesModal = featureFlags.legacyClientHealthIssuesModal;
  const useLegacyUnknownEnforcementModal = featureFlags.legacyUnknownEnforcementModal;
  const navigate = useNavigate();

  const [clientHealthModalOpen, setClientHealthModalOpen] = useState(false);
  const [selectedBucket, setSelectedBucket] = useState(null);
  const [unknownEnforcementModalOpen, setUnknownEnforcementModalOpen] = useState(false);
  // Nothing is filtered until a tag is picked; naming the key here is only so the URL can be read
  // back on the way in.
  const [filters, setFilters] = useState(() => readFilterParams({ tagId: '' }) ?? {});

  useFilterParams(filters);
  const { data: environment } = useDashboardEnvironment();
  const { data: tagOptionsData } = useTagOptions({ search: '' });
  const tagOptions = useMemo(() => tagOptionsData?.tags || [], [tagOptionsData]);
  const selectedTagId = filters.tagId ? Number(filters.tagId) : null;

  const sharedDeviceQuery = useMemo(
    () => ({
      ...(selectedTagId ? { TagId: selectedTagId } : {}),
      Statuses: [DEVICE_STATUS.Approved, DEVICE_STATUS.Unknown],
    }),
    [selectedTagId],
  );

  const enforcementDeviceQuery = useMemo(
    () => ({
      ...sharedDeviceQuery,
      HealthStatuses: [HEALTH_STATUS.Healthy],
    }),
    [sharedDeviceQuery],
  );

  const { data: devicesEnforcementData, isLoading: enforcementIsLoading } = useDashboardEnforcement(
    {
      apiQuery: enforcementDeviceQuery,
    },
  );

  const policiesQuery = useInfiniteDashboardPolicies({
    apiQuery: selectedTagId ? { TagId: selectedTagId } : {},
    limit: DISTRIBUTION_PAGE_SIZE,
  });

  const managedInstallerQuery = useInfiniteDashboardManagedInstallers({
    limit: DISTRIBUTION_PAGE_SIZE,
  });

  const { data: versionLogCollectorData, isLoading: versionLogCollectorIsLoading } =
    useDashboardLogCollectorVersions({
      apiQuery: selectedTagId ? { TagId: selectedTagId } : {},
    });

  const { data: wdacReadinessData, isLoading: wdacReadinessIsLoading } =
    useWdacReadinessStatusCounts({
      apiQuery: {
        Statuses: [DEVICE_STATUS.Approved, DEVICE_STATUS.Unknown],
        HealthStatuses: [HEALTH_STATUS.Healthy],
        WdacStatus: WDAC_STATUS.Audit,
      },
    });

  const { data: clientHealthBarData, isLoading: clientHealthBarIsLoading } =
    useDashboardClientHealth({
      apiQuery: sharedDeviceQuery,
    });

  const enforcementStatusChartData = useMemo(() => {
    const deviceCountByStatus = new Map(
      (devicesEnforcementData || []).map(item => [item.status, item.deviceCount]),
    );

    return [WDAC_STATUS.Enforced, WDAC_STATUS.Audit, WDAC_STATUS.Unknown].map(status => ({
      label: getWdacStatusLabel(status),
      value: deviceCountByStatus.get(status) || 0,
      name: status,
      color: ENFORCEMENT_COLORS[status],
    }));
  }, [devicesEnforcementData]);

  const unknownDevicesCount = useMemo(
    () => enforcementStatusChartData.find(item => item.name === WDAC_STATUS.Unknown)?.value || 0,
    [enforcementStatusChartData],
  );

  const clientHealthChartData = useMemo(
    () =>
      (clientHealthBarData || []).map(item => ({
        name: item.bucket,
        label: getHealthStatusLabel(item.bucket),
        value: item.deviceCount,
        color: CLIENT_HEALTH_CHART_COLORS[item.bucket],
      })),
    [clientHealthBarData],
  );

  const clientHealthIssuesCount = useMemo(() => {
    if (!clientHealthBarData?.length) return 0;
    const configIssueCount =
      clientHealthBarData.find(item => item.bucket === HEALTH_STATUS.ConfigIssue)?.deviceCount || 0;
    const offlineCount =
      clientHealthBarData.find(item => item.bucket === HEALTH_STATUS.Offline)?.deviceCount || 0;
    return configIssueCount + offlineCount;
  }, [clientHealthBarData]);

  const policiesChartData = useMemo(
    () =>
      policiesQuery.data?.pages?.flatMap(page =>
        (page.items || []).map(policy => ({
          id: policy.policyId,
          label: policy.policyName,
          value: policy.percentage,
          deviceCount: policy.devicesWithPolicy,
        })),
      ) || [],
    [policiesQuery.data],
  );

  const managedInstallerChartData = useMemo(
    () =>
      managedInstallerQuery.data?.pages?.flatMap(page =>
        (page.items || []).map(installer => ({
          id: installer.id,
          label: installer.binaryName,
          value: installer.percentage,
          deviceCount: installer.deviceCount,
        })),
      ) || [],
    [managedInstallerQuery.data],
  );

  const readinessChartData = useMemo(() => {
    const countByStatus = {
      [READINESS_STATUS.Ready]: wdacReadinessData?.ready || 0,
      [READINESS_STATUS.AlmostReady]: wdacReadinessData?.almostReady || 0,
      [READINESS_STATUS.NeedsWork]: wdacReadinessData?.needsWork || 0,
    };

    return Object.values(READINESS_STATUS).map(status => ({
      name: status,
      label: getReadinessStatusLabel(status),
      value: countByStatus[status],
      color: getReadinessStatusColor(status),
    }));
  }, [wdacReadinessData]);

  const versionChartData = useMemo(
    () =>
      (versionLogCollectorData || []).map((item, index) => ({
        name: item.version,
        label: item.version,
        value: item.percentage,
        valueLabel: `${item.percentage}%`,
        color: rampColor(index),
      })),
    [versionLogCollectorData],
  );

  const handleClientHealthWarningClick = useCallback(() => {
    if (!useLegacyClientHealthIssuesModal) {
      navigate(
        buildDevicesLink({
          ...sharedDeviceQuery,
          HealthStatuses: [HEALTH_STATUS.ConfigIssue, HEALTH_STATUS.Offline],
        }),
      );
      return;
    }

    setSelectedBucket(null);
    setClientHealthModalOpen(true);
  }, [navigate, sharedDeviceQuery, useLegacyClientHealthIssuesModal]);

  const handleClientHealthChartClick = useCallback(
    data => {
      if (!data?.name) {
        return;
      }

      if (data.name !== HEALTH_STATUS.Healthy) {
        if (!useLegacyClientHealthIssuesModal) {
          navigate(
            buildDevicesLink({
              ...sharedDeviceQuery,
              HealthStatuses: [data.name],
            }),
          );
          return;
        }

        setSelectedBucket(data.name);
        setClientHealthModalOpen(true);
        return;
      }

      navigate(
        buildDevicesLink({
          ...sharedDeviceQuery,
          HealthStatuses: [data.name],
        }),
      );
    },
    [navigate, sharedDeviceQuery, useLegacyClientHealthIssuesModal],
  );

  const handleUnknownEnforcementWarningClick = useCallback(() => {
    if (useLegacyUnknownEnforcementModal) {
      setUnknownEnforcementModalOpen(true);
      return;
    }

    navigate(
      buildDevicesLink({
        ...enforcementDeviceQuery,
        WdacStatuses: [WDAC_STATUS.Unknown],
      }),
    );
  }, [enforcementDeviceQuery, navigate, useLegacyUnknownEnforcementModal]);

  const onEnforcementStatusSlotClick = useCallback(
    data => {
      const wdacStatus = data?.label === 'Unknown' ? WDAC_STATUS.Unknown : data?.label;
      if (!wdacStatus) {
        return;
      }

      navigate(
        buildDevicesLink({
          ...enforcementDeviceQuery,
          WdacStatuses: [wdacStatus],
        }),
      );
    },
    [enforcementDeviceQuery, navigate],
  );

  const dashboardFilterOptions = useMemo(
    () => [
      {
        key: 'tagId',
        type: 'select',
        label: 'Device Tag',
        options: [
          { value: '', label: 'All device tags' },
          ...tagOptions.map(tag => ({ value: String(tag.id), label: tag.name })),
        ],
      },
    ],
    [tagOptions],
  );

  const onPolicyChartClick = useCallback(
    item => {
      if (!item?.id) return;

      navigate(`${PORTAL_ROUTE}/policy-details/${item.id}/general`);
    },
    [navigate],
  );

  const onManagedInstallerChartClick = useCallback(
    item => {
      if (!item?.id) return;

      // The details page reads this back for its back button, so returning here keeps whichever
      // filters the URL is currently carrying.
      sessionStorage.setItem(
        WDAC_MANAGED_INSTALLERS_PREVIOUS_PATH,
        `${window.location.pathname}${window.location.search}`,
      );
      navigate(`${PORTAL_ROUTE}/managed-installer-details/${item.id}/general`);
    },
    [navigate],
  );

  const loadMorePolicies = useCallback(() => {
    if (policiesQuery.hasNextPage) {
      policiesQuery.fetchNextPage();
    }
  }, [policiesQuery]);

  const loadMoreManagedInstallers = useCallback(() => {
    if (managedInstallerQuery.hasNextPage) {
      managedInstallerQuery.fetchNextPage();
    }
  }, [managedInstallerQuery]);

  const onVersionChartClick = useCallback(
    item => {
      if (!item?.name) return;

      navigate(
        buildDevicesLink({
          ...sharedDeviceQuery,
          LogCollectorVersion: item.name,
        }),
      );
    },
    [navigate, sharedDeviceQuery],
  );

  const onReadinessChartClick = useCallback(
    item => {
      if (!item?.name) {
        return;
      }

      const queryString = objectToQueryString({
        devicesTab: 'wdacReadiness',
        readinessStatus: item.name,
      });
      navigate(`${PORTAL_ROUTE}/devices?${queryString}`);
    },
    [navigate],
  );

  return (
    <Stack direction="row" gap={4}>
      <Stack gap={4} flex={1} width="100%">
        <Typography
          sx={{
            minHeight: '42px',
            fontSize: 28,
            fontWeight: 'bold',
          }}>
          {environment?.name ? `${environment?.name} dashboard` : ''}
        </Typography>

        <Stack direction="row" justifyContent="space-between" alignItems="center" width="100%">
          <Box width="100%">
            <ListActionBar
              filters={filters}
              onFilterChange={setFilters}
              filterOptions={dashboardFilterOptions}
            />
          </Box>
        </Stack>

        <Box sx={CHART_GRID_SX}>
          <StatusBarChart
            title="Device Enforcement State"
            subtitle="Healthy devices · click to view filtered devices"
            titleSize="lg"
            data={enforcementStatusChartData}
            onItemClick={onEnforcementStatusSlotClick}
            isLoading={enforcementIsLoading}
            warningCount={unknownDevicesCount}
            warningType="Unknown"
            onWarningClick={handleUnknownEnforcementWarningClick}
          />

          <StatusBarChart
            title="Health Status"
            subtitle="Approved and unknown devices · click to view filtered devices"
            titleSize="lg"
            data={clientHealthChartData}
            onItemClick={handleClientHealthChartClick}
            isLoading={clientHealthBarIsLoading}
            warningCount={clientHealthIssuesCount}
            warningType="Issues"
            onWarningClick={handleClientHealthWarningClick}
          />

          <StatusDonutChart
            title="Client Agent Version"
            subtitle="Share of devices per agent version · click to view filtered devices"
            titleSize="lg"
            data={versionChartData}
            onItemClick={onVersionChartClick}
            isLoading={versionLogCollectorIsLoading}
          />

          <StatusBarChart
            title="WDAC Readiness"
            subtitle="Audit-mode devices · click to view filtered devices"
            titleSize="lg"
            data={readinessChartData}
            onItemClick={onReadinessChartClick}
            isLoading={wdacReadinessIsLoading}
          />

          <DistributionBarChart
            title="Device Policies"
            subtitle="Visible, active base policies · click for policy details"
            titleSize="lg"
            data={policiesChartData}
            total={policiesQuery.data?.pages?.[0]?.total}
            isLoading={policiesQuery.isLoading}
            isLoadingMore={policiesQuery.isFetchingNextPage}
            hasMore={!!policiesQuery.hasNextPage}
            onLoadMore={loadMorePolicies}
            onItemClick={onPolicyChartClick}
            sx={FULL_WIDTH_CARD_SX}
          />

          <DistributionBarChart
            title="Managed Installer Distribution"
            subtitle="Share of devices reporting each managed installer · click for managed installer details"
            titleSize="lg"
            data={managedInstallerChartData}
            total={managedInstallerQuery.data?.pages?.[0]?.total}
            isLoading={managedInstallerQuery.isLoading}
            isLoadingMore={managedInstallerQuery.isFetchingNextPage}
            hasMore={!!managedInstallerQuery.hasNextPage}
            onLoadMore={loadMoreManagedInstallers}
            onItemClick={onManagedInstallerChartClick}
            sx={FULL_WIDTH_CARD_SX}
          />
        </Box>
      </Stack>

      {useLegacyClientHealthIssuesModal && (
        <ClientHealthIssuesModal
          open={clientHealthModalOpen}
          onClose={() => {
            setClientHealthModalOpen(false);
            setTimeout(() => setSelectedBucket(null), 200);
          }}
          selectedBucket={selectedBucket}
          tagId={selectedTagId}
        />
      )}

      {useLegacyUnknownEnforcementModal && (
        <UnknownEnforcementModal
          open={unknownEnforcementModalOpen}
          onClose={() => setUnknownEnforcementModalOpen(false)}
          tagId={selectedTagId}
        />
      )}
    </Stack>
  );
};

export default DevicesCharts;
